import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Locality } from '@/types/auth'
import axios from '@/lib/axios'
import type { ProblemDetails } from '@/types/api'

export interface CreateLocalityRequest {
  name: string
  timeZone: string
}

export interface UpdateLocalityRequest extends CreateLocalityRequest {
  isActive: boolean
}

export interface AssembleCredentials {
  hasCredentials: boolean
  username?: string
  assembleLocalityId?: string
  assembleLocalityName?: string
}

export interface SaveAssembleCredentialsRequest {
  username: string
  password?: string
  assembleLocalityId?: string
  assembleLocalityName?: string
}

export interface TestAssembleConnectionRequest {
  username: string
  password: string
}

export interface FetchAssembleEventsRequest {
  year: number
  month: number
}

export interface FetchAssembleEventsResponse {
  totalFetched: number
  rosterDraftId: string
}

export const useLocalitiesStore = defineStore('localities', () => {
  const localities = ref<Locality[]>([])
  const selectedLocalityId = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const assembleCredentials = ref<AssembleCredentials | null>(null)

  const selectedLocality = computed(() => {
    if (!selectedLocalityId.value) return null
    return localities.value.find((l) => l.id === selectedLocalityId.value) || null
  })

  function setSelectedLocalityId(id: string | null) {
    selectedLocalityId.value = id
  }

  function setSelectedLocality(locality: Locality) {
    selectedLocalityId.value = locality.id
    document.title = 'Meeting Scheduler - ' + locality.name
  }

  async function fetchLocalities(): Promise<Locality[]> {
    isLoading.value = true
    error.value = null
    try {
      const response = await axios.get<Locality[]>('/localities')
      localities.value = response.data
      return response.data
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      error.value = problem?.detail || problem?.title || 'Failed to fetch localities'
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  async function createLocality(request: CreateLocalityRequest): Promise<Locality> {
    isLoading.value = true
    error.value = null
    try {
      const response = await axios.post<Locality>('/localities', request)
      await fetchLocalities()
      return response.data
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      error.value = problem?.detail || problem?.title || 'Failed to create locality'
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  async function updateLocality(id: string, request: UpdateLocalityRequest): Promise<Locality> {
    isLoading.value = true
    error.value = null
    try {
      const response = await axios.put<Locality>(`/localities/${id}`, request)
      await fetchLocalities()
      return response.data
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      error.value = problem?.detail || problem?.title || 'Failed to update locality'
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  async function deleteLocality(id: string): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      await axios.delete(`/localities/${id}`)
      await fetchLocalities()
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      error.value = problem?.detail || problem?.title || 'Failed to delete locality'
      throw new Error(error.value)
    } finally {
      isLoading.value = false
    }
  }

  async function getAssembleCredentials(localityId: string): Promise<AssembleCredentials> {
    try {
      const response = await axios.get<AssembleCredentials>(
        `/localities/${localityId}/assemble/credentials`,
      )
      assembleCredentials.value = response.data
      return response.data
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      const message = problem?.detail || problem?.title || 'Failed to get Assemble credentials'
      throw new Error(message)
    }
  }

  async function saveAssembleCredentials(
    localityId: string,
    data: SaveAssembleCredentialsRequest,
  ): Promise<void> {
    try {
      await axios.put(`/localities/${localityId}/assemble/credentials`, data)
      await getAssembleCredentials(localityId)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      const message = problem?.detail || problem?.title || 'Failed to save Assemble credentials'
      throw new Error(message)
    }
  }

  async function testAssembleConnection(
    localityId: string,
    data: TestAssembleConnectionRequest,
  ): Promise<void> {
    try {
      await axios.post(`/localities/${localityId}/assemble/test-connection`, data)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      const message = problem?.detail || problem?.title || 'Connection test failed'
      throw new Error(message)
    }
  }

  async function fetchAssembleEvents(
    localityId: string,
    data: FetchAssembleEventsRequest,
  ): Promise<FetchAssembleEventsResponse> {
    try {
      const response = await axios.post<FetchAssembleEventsResponse>(
        `/localities/${localityId}/assemble/fetch-events`,
        data,
      )
      return response.data
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: ProblemDetails } }
      const problem = axiosError.response?.data
      const message = problem?.detail || problem?.title || 'Failed to fetch Assemble events'
      throw new Error(message)
    }
  }

  return {
    localities,
    selectedLocalityId,
    selectedLocality,
    isLoading,
    error,
    assembleCredentials,
    setSelectedLocalityId,
    setSelectedLocality,
    fetchLocalities,
    createLocality,
    updateLocality,
    deleteLocality,
    getAssembleCredentials,
    saveAssembleCredentials,
    testAssembleConnection,
    fetchAssembleEvents,
  }
})
